import { Card } from '../ui/Card';
import clsx from 'clsx';
import type { MaandTotalen } from '../../stores/controle-store';

interface MaandSamenvattingProps {
  totalen: MaandTotalen;
  isLoading: boolean;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

function verschilColor(verschil: number): string {
  const abs = Math.abs(verschil);
  if (abs > 1) return 'text-red-600 font-semibold';
  if (abs > 0) return 'text-amber-600';
  return 'text-green-600';
}

interface MethodBlockProps {
  label: string;
  externLabel: string;
  sfLabel?: string;
  extern: number;
  sf: number;
  verschil: number;
  loading?: boolean;
  noExtern?: boolean;
  extra?: React.ReactNode;
}

function MethodBlock({ label, externLabel, sfLabel = 'SF', extern, sf, verschil, loading, noExtern, extra }: MethodBlockProps) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted uppercase tracking-wider">{label}</p>
      {!noExtern && (
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-muted">{externLabel}</span>
          <span className={clsx('text-sm font-medium', loading && 'animate-pulse text-gray-300')}>
            {loading ? '...' : formatCurrency(extern)}
          </span>
        </div>
      )}
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs text-muted">{sfLabel}</span>
        <span className="text-sm font-medium">{formatCurrency(sf)}</span>
      </div>
      {!noExtern && (
        <div className="flex items-baseline justify-between gap-2 pt-1 border-t border-gray-100">
          <span className="text-xs text-muted">Verschil</span>
          <span className={clsx('text-sm', verschilColor(verschil))}>
            {formatCurrency(verschil)}
          </span>
        </div>
      )}
      {extra}
    </div>
  );
}

export function MaandSamenvatting({ totalen, isLoading }: MaandSamenvattingProps) {
  return (
    <Card variant="elevated" padding="md">
      <div className="grid grid-cols-6 gap-5">
        <MethodBlock
          label="Pin"
          externLabel="MyPos"
          extern={totalen.pin.extern}
          sf={totalen.pin.sf}
          verschil={totalen.pin.verschil}
          loading={isLoading}
          noExtern={totalen.totalMyposCount === 0}
        />
        <MethodBlock
          label="iDEAL / Mollie"
          externLabel="Mollie"
          extern={totalen.ideal.extern}
          sf={totalen.ideal.sf}
          verschil={totalen.ideal.verschil}
          loading={isLoading}
          extra={
            totalen.totalMollieCount > 0 ? (
              <div className="text-xs text-muted pt-1">
                {totalen.matchedCount}/{totalen.totalMollieCount} gematcht
              </div>
            ) : null
          }
        />
        <MethodBlock
          label="Bank"
          externLabel="Extern"
          extern={totalen.bank.extern}
          sf={totalen.bank.sf}
          verschil={totalen.bank.verschil}
        />
        <div className="space-y-1">
          <p className="text-xs text-muted uppercase tracking-wider">Contant</p>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted">Geregistreerd</span>
            <span className="text-sm font-medium">{formatCurrency(totalen.contant.geregistreerd)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted">Gecontroleerd</span>
            <span className="text-sm font-medium">{formatCurrency(totalen.contant.gecontroleerd)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted">Envelop</span>
            <span className="text-sm font-medium">{formatCurrency(totalen.contant.envelop)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-2 pt-1 border-t border-gray-100">
            <span className="text-xs text-muted">Verschil</span>
            <span className={clsx('text-sm', verschilColor(totalen.contant.verschil))}>
              {formatCurrency(totalen.contant.verschil)}
            </span>
          </div>
        </div>
        {/* Refunds */}
        <div className="space-y-1">
          <p className="text-xs text-muted uppercase tracking-wider">Refunds</p>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted">Mollie</span>
            <span className={clsx('text-sm font-medium', totalen.refunds > 0 && 'text-red-600')}>
              {totalen.refunds > 0 ? `-${formatCurrency(totalen.refunds)}` : formatCurrency(0)}
            </span>
          </div>
          {totalen.refunds > 0 && (
            <div className="flex items-baseline justify-between gap-2 pt-1 border-t border-gray-100">
              <span className="text-xs text-muted">Netto</span>
              <span className="text-sm font-medium">
                {formatCurrency(totalen.totaalMollie - totalen.refunds)}
              </span>
            </div>
          )}
        </div>
        {/* Total */}
        <div className="space-y-1">
          <p className="text-xs text-muted uppercase tracking-wider">Totaal SF</p>
          <p className={clsx('text-xl font-semibold text-main mt-1', isLoading && 'animate-pulse text-gray-300')}>
            {isLoading ? '...' : formatCurrency(totalen.totaalSf)}
          </p>
        </div>
      </div>
    </Card>
  );
}
